<script setup lang="ts">
import { computed, inject } from 'vue';
import { NxButton } from '../Button';
import { NxDialog } from '../Dialog';
import { NxSwitch } from '../Inputs';
import { i18nInjectionKey } from '../internal';
import { NxToggleGroup, type NxToggleGroupOption } from '../ToggleGroup';
import type { NxTableRowDensity } from './internal';

/**
 * Table preferences modal (gear → Einstellungen): search-row visibility,
 * always-visible scrollbars, row density, zebra stripes, grid lines, the
 * header's brand fill and top-aligned cells (settings key `table`, I-2510).
 * „Gilt für" (`scope`, on a table with a settings key) picks what a change
 * sets: this table's own settings or every table's. „Standard
 * wiederherstellen" (`reset`) drops what the scope holds — this table's own
 * settings, or the user's defaults for every table; „Überall übernehmen"
 * (`apply-everywhere`) drops every table's own settings. A setting a tenant
 * or company locked (`locked`) shows greyed out: it cannot be changed here.
 * Filter management stays in NxTableFilterManageDialog.
 */
const isOpen = defineModel<boolean>('open', { required: true });
/** Whether the quick filter row ("Suchleiste") under the header is shown. */
const searchRowVisible = defineModel<boolean>('searchRowVisible', { required: true });
/** Whether classic scrollbars stay visible on the table scroller. */
const alwaysShowScrollbars = defineModel<boolean>('alwaysShowScrollbars', { required: true });
/** Alternating row fill (every other body row) — the user's default for every table. */
const zebraStripes = defineModel<boolean>('zebraStripes', { required: true });
/** Horizontal separators between body rows. */
const horizontalLines = defineModel<boolean>('horizontalLines', { required: true });
/** Vertical rules between data columns. */
const verticalLines = defineModel<boolean>('verticalLines', { required: true });
/** Row height step (airy / normal / compact). */
const rowDensity = defineModel<NxTableRowDensity>('rowDensity', { default: 'normal' });
/** The header row filled with the brand colour (`table.header: brand`). */
const headerBrand = defineModel<boolean>('headerBrand', { default: false });
/** Cells at the top of a taller row (`table.cell_align: top`). */
const cellsTop = defineModel<boolean>('cellsTop', { default: false });

/** What a change sets: this table's own settings or every table's. */
const scope = defineModel<'table' | 'all'>('scope', { default: 'all' });

const props = defineProps<{
  hasFilterRow: boolean;
  /** This table fixes its own stripes (`zebra` prop): the switch still sets every other table's. */
  zebraFixed?: boolean;
  /** The table can keep settings of its own (it has a settings key): „Gilt für" shows. */
  scoped?: boolean;
  /** How many settings this table sets over everybody's. */
  ownCount?: number;
  /** How many tables have settings of their own. */
  tablesWithOwn?: number;
  /** The `table` fields a tenant or company locked (`zebra`, `row_density`, …): greyed out. */
  locked?: readonly string[];
}>();

/** A setting the user cannot change here: an earlier layer locked its field. */
function isLocked(field: string): boolean {
  return props.locked?.includes(field) === true;
}

const anyLocked = computed(() => (props.locked?.length ?? 0) > 0);

const emit = defineEmits<{
  /** Back to the defaults of the scope: this table's own settings, or the user's for every table. */
  reset: [];
  /** Every table's own settings go: everybody's apply in every table. */
  'apply-everywhere': [];
}>();

const i18n = inject(i18nInjectionKey)!;

const scopeOptions = computed<NxToggleGroupOption<'table' | 'all'>[]>(() => [
  { value: 'table', label: i18n.t('common.table.preferences.scope_table') },
  { value: 'all', label: i18n.t('common.table.preferences.scope_all') },
]);

const densityOptions = computed<NxToggleGroupOption<NxTableRowDensity>[]>(() => [
  { value: 'airy', label: i18n.t('common.table.preferences.density_airy') },
  { value: 'normal', label: i18n.t('common.table.preferences.density_normal') },
  { value: 'compact', label: i18n.t('common.table.preferences.density_compact') },
]);
</script>

<template>
  <NxDialog
    v-model:open="isOpen"
    :title="i18n.t('common.table.preferences.title')"
    :description="i18n.t('common.table.preferences.description')"
    :resizable="false"
  >
    <template #content>
      <div class="preferences-body">
        <section v-if="props.scoped" class="preferences-section">
          <div class="choice-row">
            <span>{{ i18n.t('common.table.preferences.scope') }}</span>
            <NxToggleGroup
              v-model="scope"
              :options="scopeOptions"
              :aria-label="i18n.t('common.table.preferences.scope')"
              height="xs"
              data-testid="table-preferences-scope"
            />
          </div>
          <p
            v-if="scope === 'table' && (props.ownCount ?? 0) > 0"
            class="section-hint"
            data-testid="table-preferences-own"
          >
            {{ i18n.t('common.table.preferences.own_hint', { count: props.ownCount ?? 0 }) }}
          </p>
          <p
            v-else-if="scope === 'all' && (props.tablesWithOwn ?? 0) > 0"
            class="section-hint"
            data-testid="table-preferences-tables-with-own"
          >
            {{
              i18n.t('common.table.preferences.tables_with_own', {
                count: props.tablesWithOwn ?? 0,
              })
            }}
          </p>
        </section>
        <p v-if="anyLocked" class="section-hint" data-testid="table-preferences-locked">
          {{ i18n.t('common.table.preferences.locked_hint') }}
        </p>
        <section v-if="props.hasFilterRow" class="preferences-section">
          <h3 class="section-title">{{ i18n.t('common.table.preferences.search') }}</h3>
          <label class="switch-row">
            <NxSwitch v-model="searchRowVisible" height="xs" :disabled="isLocked('search_row')" />
            <span>{{ i18n.t('common.table.preferences.search_row') }}</span>
          </label>
        </section>

        <section class="preferences-section">
          <h3 class="section-title">{{ i18n.t('common.table.preferences.appearance') }}</h3>
          <div class="choice-row">
            <span>{{ i18n.t('common.table.preferences.row_density') }}</span>
            <NxToggleGroup
              v-model="rowDensity"
              :options="densityOptions"
              :aria-label="i18n.t('common.table.preferences.row_density')"
              :disabled="isLocked('row_density')"
              height="xs"
            />
          </div>
          <label class="switch-row">
            <NxSwitch v-model="zebraStripes" height="xs" :disabled="isLocked('zebra')" />
            <span>{{ i18n.t('common.table.preferences.zebra_stripes') }}</span>
          </label>
          <p v-if="props.zebraFixed" class="section-hint" data-testid="nx-table-zebra-fixed">
            {{ i18n.t('common.table.preferences.zebra_fixed') }}
          </p>
          <label class="switch-row">
            <NxSwitch
              v-model="horizontalLines"
              height="xs"
              :disabled="isLocked('horizontal_lines')"
            />
            <span>{{ i18n.t('common.table.preferences.horizontal_lines') }}</span>
          </label>
          <label class="switch-row">
            <NxSwitch v-model="verticalLines" height="xs" :disabled="isLocked('vertical_lines')" />
            <span>{{ i18n.t('common.table.preferences.vertical_lines') }}</span>
          </label>
          <label class="switch-row">
            <NxSwitch
              v-model="headerBrand"
              height="xs"
              :disabled="isLocked('header')"
              data-testid="table-preferences-header-brand"
            />
            <span>{{ i18n.t('common.table.preferences.header_brand') }}</span>
          </label>
          <label class="switch-row">
            <NxSwitch
              v-model="cellsTop"
              height="xs"
              :disabled="isLocked('cell_align')"
              data-testid="table-preferences-cells-top"
            />
            <span>{{ i18n.t('common.table.preferences.cells_top') }}</span>
          </label>
        </section>

        <section class="preferences-section">
          <h3 class="section-title">{{ i18n.t('common.table.preferences.scroll') }}</h3>
          <label class="switch-row">
            <NxSwitch
              v-model="alwaysShowScrollbars"
              height="xs"
              :disabled="isLocked('always_show_scrollbars')"
            />
            <span>{{ i18n.t('common.table.preferences.always_show_scrollbars') }}</span>
          </label>
        </section>
      </div>
    </template>
    <template #buttons="{ close }">
      <NxButton variant="outline" data-testid="table-preferences-reset" @click="emit('reset')">
        {{
          props.scoped && scope === 'table'
            ? i18n.t('common.table.preferences.reset_table')
            : i18n.t('common.table.preferences.reset')
        }}
      </NxButton>
      <NxButton
        v-if="scope === 'all' && (props.tablesWithOwn ?? 0) > 0"
        variant="outline"
        data-testid="table-preferences-apply-everywhere"
        @click="emit('apply-everywhere')"
      >
        {{ i18n.t('common.table.preferences.apply_everywhere') }}
      </NxButton>
      <NxButton @click="close">
        {{ i18n.t('common.table.preferences.close') }}
      </NxButton>
    </template>
  </NxDialog>
</template>

<style scoped>
.preferences-body {
  width: min(var(--nxt-panel-width-xl), 92vw);
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
}

.preferences-section {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

.section-title {
  margin: 0;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--nxt-fg-muted);
}

.switch-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  cursor: pointer;
  font-size: var(--nxt-font-size-sm);
}

.choice-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-xs);
  font-size: var(--nxt-font-size-sm);
}

.switch-row :deep(.switch) {
  margin-block: 0.125em;
  margin-inline: 0;
}

.section-hint {
  margin: 0;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
</style>
